import React, { useState, useRef, useEffect } from 'react';

const TermsOfService = () => {
    const [activeSection, setActiveSection] = useState(null);
    const sectionRefs = useRef({});

    const scrollToSection = (sectionId) => {
        setActiveSection(sectionId);
        sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        { id: 'introduction', title: '1. Introduction' },
        { id: 'definitions', title: '2. Definitions' },
        { id: 'eligibility', title: '3. Eligibility' },
        { id: 'account', title: '4. Account Registration & Security' },
        { id: 'use-platform', title: '5. Use of the Platform' },
        { id: 'prohibited', title: '6. Prohibited Activities' },
        { id: 'paid-services', title: '7. Paid Services & Payments' },
        { id: 'disclaimers', title: '8. Disclaimers & Limitation of Liability' },
        { id: 'intellectual-property', title: '9. Intellectual Property' },
        { id: 'termination', title: '10. Termination' },
        { id: 'governing-law', title: '11. Governing Law & Dispute Resolution' },
        { id: 'contact', title: '12. Contact Us' },
    ];

    return (
        <div className="max-w-6xl mx-auto p-4 bg-white">
            {/* Header */}
            <div className="text-center mb-10">
                <h1 className="text-4xl font-bold text-blue-800 mb-2">Terms of Service</h1>
                <p className="text-xl text-gray-600">IndiaHirings.com</p>
                <p className="text-gray-500 mt-2">Operated by: YaGa Technologies</p>
                <p className="text-gray-500 mt-1">Last Updated: January 1, 2026</p>
            </div>

            {/* Introduction */}
            <div className="bg-blue-50 p-6 rounded-lg shadow-sm mb-8">
                <p className="text-gray-800 leading-relaxed">
                    Welcome to IndiaHirings.com. These Terms of Service ("Terms") govern your access to and use of our website, mobile applications, and related services. By accessing, registering, or using the Platform, you acknowledge that you have read, understood, and agreed to be bound by these Terms and our Privacy Policy.
                </p>
                <p className="text-gray-800 leading-relaxed mt-4 font-medium">
                    Please read these Terms carefully before using our services.
                </p>
            </div>

            {/* Table of Contents */}
            <div className="mb-10">
                <h2 className="text-2xl font-bold text-blue-800 mb-4">Table of Contents</h2>
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => scrollToSection(section.id)}
                                className={`text-left p-2 rounded-md transition-colors hover:bg-blue-50 ${activeSection === section.id ? 'bg-blue-100 text-blue-800' : 'text-gray-700'
                                    }`}
                            >
                                {section.title}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content Sections */}
            <div className="space-y-10">
                {/* Section 1 - Introduction */}
                <div
                    ref={(el) => (sectionRefs.current['introduction'] = el)}
                    id="introduction"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">1. Introduction</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li>
                                <span className="font-medium">1.1 Agreement:</span> These Terms of Service ("Terms") govern your access to and use of the Indiahirings.com website, mobile applications, and related services (collectively, the "Platform").
                            </li>
                            <li>
                                <span className="font-medium">1.2 Contracting Parties:</span> These Terms form a legally binding agreement between YaGa Technologies ("Indiahirings", "we", "our", or "us") and any individual or entity using the Platform ("User", "you", or "your").
                            </li>
                            <li>
                                <span className="font-medium">1.3 Acceptance:</span> By accessing, registering, or using the Platform, you acknowledge that you have read, understood, and agreed to be bound by these Terms and our Privacy Policy.
                            </li>
                            <li>
                                <span className="font-medium">1.4 Compliance:</span> This document is an electronic record under the Information Technology Act, 2000 and applicable rules. It is generated by a computer system and does not require physical or digital signatures.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 2 - Definitions */}
                <div
                    ref={(el) => (sectionRefs.current['definitions'] = el)}
                    id="definitions"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">2. Definitions</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <ul className="list-disc pl-6 text-gray-700 space-y-3">
                            <li>
                                <span className="font-medium">"Platform":</span> The Indiahirings.com website, mobile apps, and all associated recruitment services.
                            </li>
                            <li>
                                <span className="font-medium">"User":</span> Any person or entity (Candidate or Employer) accessing the Platform.
                            </li>
                            <li>
                                <span className="font-medium">"Employer":</span> A registered User (individual or entity) who posts employment opportunities.
                            </li>
                            <li>
                                <span className="font-medium">"Candidate":</span> A User seeking employment opportunities.
                            </li>
                            <li>
                                <span className="font-medium">"Paid Services":</span> Premium features including job boosts, database access, subscription plans, and AI hiring tools.
                            </li>
                            <li>
                                <span className="font-medium">"Applicable Laws":</span> All Indian statutes, regulations, and governmental requirements in force.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 3 - Eligibility */}
                <div
                    ref={(el) => (sectionRefs.current['eligibility'] = el)}
                    id="eligibility"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">3. Eligibility</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-gray-700 mb-4">
                            By using the Platform, you represent and warrant that:
                        </p>
                        <ol className="list-decimal pl-6 text-gray-700 space-y-3">
                            <li>You are at least 18 years of age.</li>
                            <li>You possess the legal capacity to enter into a binding contract.</li>
                            <li>You are not barred from using the Platform under any applicable laws.</li>
                            <li>If acting on behalf of a company, you have the express authority to bind that entity to these Terms.</li>
                        </ol>
                    </div>
                </div>

                {/* Section 4 - Account Registration & Security */}
                <div
                    ref={(el) => (sectionRefs.current['account'] = el)}
                    id="account"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">4. Account Registration & Security</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li>
                                <span className="font-medium">4.1 Accuracy:</span> You must provide accurate and complete information during registration.
                            </li>
                            <li>
                                <span className="font-medium">4.2 Confidentiality:</span> You are solely responsible for maintaining the secrecy of your login credentials.
                            </li>
                            <li>
                                <span className="font-medium">4.3 Liability:</span> You are responsible for all activities occurring under your account. You must notify us immediately of any unauthorized use.
                            </li>
                            <li>
                                <span className="font-medium">4.4 Communication:</span> By creating an account, you consent to receive service updates, promotional materials, and notifications via Email, SMS, WhatsApp, or Push Notifications.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 5 - Use of the Platform */}
                <div
                    ref={(el) => (sectionRefs.current['use-platform'] = el)}
                    id="use-platform"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">5. Use of the Platform</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        {/* 5.1 For Employers */}
                        <h3 className="text-lg font-semibold text-blue-700 mb-3">5.1 For Employers</h3>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                            <li>Employers must provide accurate job descriptions, compensation ranges, and required qualifications.</li>
                            <li>
                                <span className="font-medium">Prohibition:</span> Employers shall not post "ghost jobs" (non-existent roles) or use the platform for multi-level marketing (MLM) or any fraudulent schemes.
                            </li>
                            <li>
                                <span className="font-medium">Data Retention:</span> Employers must not retain candidate data longer than necessary for the hiring process and must comply with data privacy standards.
                            </li>
                        </ul>

                        {/* 5.2 For Candidates */}
                        <h3 className="text-lg font-semibold text-blue-700 mb-3">5.2 For Candidates</h3>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>Candidates must ensure that all resume details and professional history provided are truthful.</li>
                            <li>Indiahirings does not guarantee employment or a response from any Employer.</li>
                        </ul>
                    </div>
                </div>

                {/* Section 6 - Prohibited Activities */}
                <div
                    ref={(el) => (sectionRefs.current['prohibited'] = el)}
                    id="prohibited"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">6. Prohibited Activities</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-gray-700 mb-4">Users are strictly prohibited from:</p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-3">
                            <li>
                                <span className="font-medium">Scraping:</span> Using automated tools (bots, spiders) to extract data from the Platform.
                            </li>
                            <li>
                                <span className="font-medium">Malware:</span> Distributing viruses, worms, or any code of a destructive nature.
                            </li>
                            <li>
                                <span className="font-medium">Harassment:</span> Using the Platform to threaten, harass, or abuse other Users.
                            </li>
                            <li>
                                <span className="font-medium">IP Infringement:</span> Uploading content that violates copyrights, trademarks, or trade secrets.
                            </li>
                            <li>
                                <span className="font-medium">Reverse Engineering:</span> Attempting to derive the source code of the Platform.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 7 - Paid Services & Payments */}
                <div
                    ref={(el) => (sectionRefs.current['paid-services'] = el)}
                    id="paid-services"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">7. Paid Services & Payments</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li>
                                <span className="font-medium">7.1 Activation:</span> Paid services are activated only upon receipt of full payment.
                            </li>
                            <li>
                                <span className="font-medium">7.2 Modifications:</span> Pricing and subscription tiers are subject to change at our discretion.
                            </li>
                            <li>
                                <span className="font-medium">7.3 No Guarantees:</span> Purchasing a premium listing does not guarantee a successful hire; it only increases visibility/access as described in the plan.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 8 - Disclaimers & Limitation of Liability */}
                <div
                    ref={(el) => (sectionRefs.current['disclaimers'] = el)}
                    id="disclaimers"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">8. Disclaimers & Limitation of Liability</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li>
                                <span className="font-medium">8.1 "As-Is" Basis:</span> The Platform is provided without any warranties, express or implied. We do not guarantee 100% uptime.
                            </li>
                            <li>
                                <span className="font-medium">8.2 Third-Party Content:</span> We are not responsible for the accuracy of job posts or candidate profiles. Users should perform their own due diligence.
                            </li>
                            <li>
                                <span className="font-medium">8.3 Liability Cap:</span> To the maximum extent permitted by law, the total liability of YaGa Technologies for any claim arising out of these Terms shall not exceed INR 1,000.
                            </li>
                            <li>
                                <span className="font-medium">8.4 Indirect Damages:</span> We shall not be liable for any loss of profits, data, or business opportunities.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 9 - Intellectual Property */}
                <div
                    ref={(el) => (sectionRefs.current['intellectual-property'] = el)}
                    id="intellectual-property"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">9. Intellectual Property</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-gray-700">
                            All rights, titles, and interests in the Platform (including software, design, and branding) remain the exclusive property of YaGa Technologies. Users are granted a limited, revocable license for professional use only.
                        </p>
                    </div>
                </div>

                {/* Section 10 - Termination */}
                <div
                    ref={(el) => (sectionRefs.current['termination'] = el)}
                    id="termination"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">10. Termination</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-gray-700 mb-4">
                            We reserve the right to suspend or terminate your account without notice if:
                        </p>
                        <ul className="list-disc pl-6 text-gray-700 space-y-2">
                            <li>You breach these Terms.</li>
                            <li>You engage in fraudulent or illegal activities.</li>
                            <li>Requested by law enforcement or government agencies.</li>
                        </ul>
                    </div>
                </div>

                {/* Section 11 - Governing Law & Dispute Resolution */}
                <div
                    ref={(el) => (sectionRefs.current['governing-law'] = el)}
                    id="governing-law"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">11. Governing Law & Dispute Resolution</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <ul className="list-disc pl-6 text-gray-700 space-y-4">
                            <li>
                                <span className="font-medium">11.1 Jurisdiction:</span> These Terms are governed by the laws of India.
                            </li>
                            <li>
                                <span className="font-medium">11.2 Venue:</span> Any legal proceedings shall be subject to the exclusive jurisdiction of the courts in Bhopal, Madhya Pradesh.
                            </li>
                            <li>
                                <span className="font-medium">11.3 Arbitration:</span> Disputes may be resolved through binding arbitration under the Arbitration and Conciliation Act, 1996.
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Section 12 - Contact Us */}
                <div
                    ref={(el) => (sectionRefs.current['contact'] = el)}
                    id="contact"
                    className="scroll-mt-4"
                >
                    <h2 className="text-2xl font-bold text-blue-800 mb-4">12. Contact Us</h2>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <p className="text-gray-700 mb-4">
                            For any grievances or support requests, please contact:
                        </p>
                        <div className="bg-blue-50 p-4 rounded-md">
                            <p className="font-medium text-blue-800">YaGa Technologies</p>
                            <p className="text-blue-800">support@indiahirings.com</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Back to Top Button */}
            <div className="fixed bottom-6 right-6">
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                    aria-label="Back to top"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default TermsOfService;